import { useEffect, useRef, useState } from 'react';
import { Bell, AlertTriangle, Check, X } from 'lucide-react';
import {
  useNotifications,
  useNotificationsCount,
  useDismissNotification,
  useResolveNotification,
} from '../hooks/useNotifications.js';
import { formatCurrency, formatDate } from '../lib/format.js';

/**
 * Bouton cloche avec badge + popover listant les conflits de sync ouverts.
 * Le popover fournit Garder / Adopter par conflit.
 */
export default function NotificationsButton() {
  const { data: count = 0 } = useNotificationsCount('open');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Fermeture au click extérieur + ESC
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hasNotifs = count > 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
          hasNotifs
            ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
            : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
        }`}
        aria-label={`${count} notification${count > 1 ? 's' : ''}`}
        aria-expanded={open}
      >
        <Bell size={16} />
        {hasNotifs && (
          <span className="absolute -top-1 -right-1 text-[10px] font-semibold bg-amber-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center tabular-nums leading-none">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-40 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle size={14} className="text-amber-600 shrink-0" />
              <h3 className="text-sm font-semibold text-slate-800 truncate">
                Conflits de sync
                {hasNotifs && <span className="ml-1 text-slate-400 tabular-nums">({count})</span>}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Fermer"
            >
              <X size={14} />
            </button>
          </div>
          <NotifList />
        </div>
      )}
    </div>
  );
}

function NotifList() {
  const { data, isLoading } = useNotifications('open');
  const notifs = Array.isArray(data) ? data : [];

  if (isLoading) {
    return <p className="text-sm text-slate-400 text-center py-6">Chargement…</p>;
  }
  if (!notifs.length) {
    return <p className="text-sm text-slate-400 text-center py-6">Aucun conflit ouvert.</p>;
  }

  return (
    <ul className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
      {notifs.map((n) => (
        <NotifRow key={n.id} n={n} />
      ))}
    </ul>
  );
}

function NotifRow({ n }) {
  const dismiss = useDismissNotification();
  const resolve = useResolveNotification();
  const busy = dismiss.isPending || resolve.isPending;

  const txName = n.tx_counterparty ?? n.tx_label ?? 'Transaction';
  const amount = n.tx_amount_cents != null ? formatCurrency(Math.abs(n.tx_amount_cents)) : null;
  const date   = n.tx_operation_date ? formatDate(n.tx_operation_date) : null;

  const current  = n.current_value?.category_name  ?? '—';
  const incoming = n.incoming_value?.category_name ?? '—';

  return (
    <li className="px-4 py-3 space-y-2">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{txName}</p>
        <p className="text-[11px] text-slate-500 tabular-nums">
          {date && <>{date}</>}
          {date && amount && <> · </>}
          {amount}
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5 text-xs">
        <span className="bg-slate-50 border border-slate-200 text-slate-700 px-2 py-0.5 rounded">
          <span className="text-slate-400">Ma cat :</span>{' '}
          <span className="font-medium">{current}</span>
        </span>
        <span className="bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 rounded">
          <span className="text-amber-600">Qonto :</span>{' '}
          <span className="font-medium">{incoming}</span>
        </span>
      </div>

      <div className="flex gap-1.5 justify-end">
        <button
          type="button"
          onClick={() => dismiss.mutate(n.id)}
          disabled={busy}
          className="text-xs px-2.5 py-1 rounded-md bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
        >
          <X size={11} /> Garder
        </button>
        <button
          type="button"
          onClick={() => resolve.mutate(n.id)}
          disabled={busy}
          className="text-xs px-2.5 py-1 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
        >
          <Check size={11} /> Adopter Qonto
        </button>
      </div>
    </li>
  );
}
